import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { formatPercent } from '../../lib/format';
import { ScoreGauge } from '../creatives/detail/shared/ScoreGauge';
import { InfoTip } from '../media/shared/InfoTip';
import { DashboardCard } from './DashboardCard';
import type { HeroMetricsData } from './HeroMetrics';
import { TALL_CARD_HEIGHT } from './layout';
import { useTrackTooltip } from './TooltipTracking';
import { TOOLTIPS } from './tooltips';

const RING_SIZE = 132;

interface SubMetricProps {
  label: string;
  tip: string;
  value: string;
}

function SubMetric({ label, tip, value }: SubMetricProps): ReactNode {
  const trackTooltip = useTrackTooltip();
  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Typography
          sx={{
            textTransform: 'uppercase',
            fontWeight: 600,
            fontSize: 10,
            color: 'text.secondary',
          }}
        >
          {label}
        </Typography>
        <InfoTip text={tip} onOpen={() => trackTooltip(label)} />
      </Box>
      <Typography sx={{ fontSize: 20, fontWeight: 700, color: 'text.primary' }}>{value}</Typography>
    </Box>
  );
}

export function CreativeHealthCard({
  data,
}: {
  data: HeroMetricsData['creativeHealth'];
}): ReactNode {
  return (
    <DashboardCard
      title="Creative Health Score"
      tip={TOOLTIPS.creativeHealth}
      height={TALL_CARD_HEIGHT}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
        }}
      >
        <ScoreGauge
          score={data.score}
          previous={data.previousScore}
          size={RING_SIZE}
          deltaVariant="chip"
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 3,
          borderTop: '1px solid',
          borderColor: 'divider',
          pt: 2,
        }}
      >
        <SubMetric
          label="Creative quality"
          tip={TOOLTIPS.creativeQuality}
          value={data.creativeQuality === null ? '-' : (data.creativeQuality / 10).toFixed(1)}
        />
        <SubMetric
          label="Budget efficiency"
          tip={TOOLTIPS.budgetEfficiency}
          value={formatPercent(data.budgetEfficiency, 0)}
        />
        <SubMetric
          label="Fatigue exposure"
          tip={TOOLTIPS.fatigueExposure}
          value={formatPercent(data.fatigueExposure, 0)}
        />
      </Box>
    </DashboardCard>
  );
}
